import { useMemo } from 'react';
import { PURCHASING_FLAGS } from '@notifi/shared-types/feature-flags';
import { useFeatureFlag } from '@/stores/tenant.store';

/** The two purchasing flags as the app reads them. */
export interface PurchasingFeatures {
  purchaseOrders: boolean;
  inventory: boolean;
}

/**
 * Purchasing and inventory are feature flags (`purchasing.purchase-orders`
 * and `purchasing.inventory`, PLAT-FF-1), rolled out per tenant from the
 * provisioner. `FeatureFlagsSync` keeps every flag in the tenant store, so
 * these are plain store reads: no request per component, and a flip in the
 * provisioner lands within the sync's refresh, with no reload. While a flag
 * is off the app offers no way into that area: its nav section, settings
 * page, the job's purchase orders card and the home tiles are left out, and
 * the API refuses the routes. Both read as off until the flags load.
 */
export function usePurchasingFeatures(): PurchasingFeatures {
  const purchaseOrders = useFeatureFlag(PURCHASING_FLAGS.purchaseOrders);
  const inventory = useFeatureFlag(PURCHASING_FLAGS.inventory);
  return useMemo(() => ({ purchaseOrders, inventory }), [purchaseOrders, inventory]);
}

export const usePurchasingEnabled = () => useFeatureFlag(PURCHASING_FLAGS.purchaseOrders);
export const useInventoryEnabled = () => useFeatureFlag(PURCHASING_FLAGS.inventory);

/** The permission groups (resources) that belong to purchase orders. */
const PURCHASING_RESOURCES = new Set([
  'purchase_orders',
  'supplier_bills',
  'card_transactions',
  'field_purchases',
  'part_requirements',
  'purchasing',
]);
/** The permission groups that belong to inventory. */
const INVENTORY_RESOURCES = new Set(['inventory']);
/** The built-in roles that only make sense with a flag on. */
const FEATURE_ROLES: Record<string, keyof PurchasingFeatures> = {
  purchasing_manager: 'purchaseOrders',
  warehouse_manager: 'inventory',
};

/**
 * What the roles settings show of purchasing and inventory (PLAT-FF-1): a
 * company without a flag sees neither its permission groups nor its built-in
 * role. Only what is shown changes; a role keeps every permission it holds.
 */
export function usePurchasingRoleVisibility() {
  const features = usePurchasingFeatures();
  return useMemo(
    () => ({
      showResource: (resource: string) =>
        PURCHASING_RESOURCES.has(resource)
          ? features.purchaseOrders
          : INVENTORY_RESOURCES.has(resource)
            ? features.inventory
            : true,
      showSystemRole: (name: string) => {
        const feature = FEATURE_ROLES[name];
        return !feature || features[feature];
      },
    }),
    [features],
  );
}
